<script setup lang="ts">
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import Routes from '@/constants/Routes';
import { loginErrorMessage, resolveReturnUrl, routeAfterLogin } from '@/helpers/loginHelpers';
import { useMicrosoftSignIn } from '@/composables/useMicrosoftSignIn';
import { MicrosoftProvider } from '@/services/entra/microsoftSignIn';
import { useAuthStore } from '@/services/stores/authStore';
import { useLoginService } from '@/services/api/loginService';
import type { ILoginRequest, ILoginResponse } from '@/types/interfaces/LoginTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxButtonItem, DxButtonOptions, DxForm, DxGroupItem, DxRequiredRule, DxSimpleItem } from 'devextreme-vue/form';
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore } from '@/services/stores/alertStore';

definePage({
  name: 'LoginRoot',
  meta: {
    anonymous: true
  }
});

const alert = useAlertStore();
const auth = useAuthStore();
const api = useLoginService();
const router = useRouter();
const route = useRoute();
const loading = ref(false);
const login = ref<ILoginRequest>({
  username: '',
  password: ''
});

async function signedIn(response: ILoginResponse): Promise<void> {
  auth.initialize(response.token);
  await router.push(routeAfterLogin(auth, response.requireMfa, resolveReturnUrl(router, route.query)));
}

const microsoft = useMicrosoftSignIn(signedIn);
const microsoftLoading = microsoft.loading;

async function onSubmit(): Promise<void> {
  loading.value = true;
  const response = await api.login(login.value);
  if (!response || !response.success || !response.data) {
    alert.showError(loginErrorMessage(response.problem));
    loading.value = false;
    return;
  }

  await signedIn(response.data);
}

onMounted(() => auth.initialize());
</script>

<template>
  <p class="lead text-center">
    {{ $t('login.signIn') }}
  </p>

  <form @submit.prevent="onSubmit">
    <DxForm :disabled="loading || microsoftLoading" :form-data="login" label-mode="floating">
      <DxSimpleItem data-field="username">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.userName') })" />
      </DxSimpleItem>
      <DxSimpleItem :editor-options="{ mode: 'password' }" data-field="password">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.password') })" />
      </DxSimpleItem>

      <DxGroupItem :col-count="2">
        <DxButtonItem>
          <DxButtonOptions :use-submit-behavior="true" type="primary" template="loginButtonTemplate" width="100%" />
        </DxButtonItem>
        <DxButtonItem>
          <DxButtonOptions styling-mode="text" template="forgotPasswordTemplate" width="100%" />
        </DxButtonItem>
      </DxGroupItem>

      <template #loginButtonTemplate>
        <SubmitButtonContent :icon="Icons.ArrowRightToBracket" :loading="loading" :text="$t('login.login')" />
      </template>

      <template #forgotPasswordTemplate>
        <ButtonContent :to="{ name: Routes.Login.ForgotPassword }" :text="$t('login.forgotPassword')" />
      </template>
    </DxForm>
  </form>

  <div v-if="microsoft.enabled" class="mt-4" @focusin="microsoft.prepare()" @pointerenter="microsoft.prepare()">
    <p class="text-center text-body-secondary mb-3">{{ $t('login.or') }}</p>
    <DxButton :disabled="loading || microsoftLoading" styling-mode="outlined" width="100%" @click="microsoft.signIn()">
      <SubmitButtonContent :icon="Icons.Microsoft" :loading="microsoftLoading" :text="$t('login.signInWith', { provider: MicrosoftProvider })" />
    </DxButton>
  </div>
</template>

<style scoped>

</style>